import { Environment } from '@/config/environment';
import {
  DESKTOP_MEDIA_BP,
  MEDIA_DOMAIN,
  MEDIA_PREFIX,
  MOBILE_MEDIA_BP,
  RECIPE_DOMAIN,
  RECIPE_DOMAIN_AZURE,
  RECIPE_PREFIX,
  RECIPE_PREFIX_EDGE,
} from 'config/config';

import type { AiLabelType, HeroBannerImages, RecipeImage, RecipeResult } from 'types/Recipes';

const MOBILE_IMAGE_WIDTH = 290;
const MOBILE_IMAGE_HEIGHT = 400;
const DESKTOP_IMAGE_WIDTH = 1272;

export const transformUrlToProxyUrl = (src: string) => {
  const { IMAGE_PROXY_ENABLED } = Environment.getBuildtimeEnvVars();

  if (!src) return '';

  if (!IMAGE_PROXY_ENABLED) {
    return src;
  }

  if (src.startsWith('/') || !src.startsWith('http') || !src.startsWith('https')) {
    return src;
  }

  try {
    const tempUrl = new URL(src);
    const origin = tempUrl.origin;

    if (tempUrl?.host === MEDIA_DOMAIN) {
      return tempUrl.href.replace(origin, MEDIA_PREFIX);
    }

    if (tempUrl?.host.startsWith(RECIPE_DOMAIN)) {
      return tempUrl.href.replace(origin, RECIPE_PREFIX);
    }

    if (tempUrl?.host.startsWith(RECIPE_DOMAIN_AZURE)) {
      return tempUrl.href.replace(origin, RECIPE_PREFIX_EDGE);
    }
  } catch (error) {
    console.error('Error while transforming src', { error, src });
    return src;
  }

  return src;
};

const transformRecipeImageToProxyUrl = (
  formats: { Url: string; Width: number; Height: number }[],
) => {
  return formats.map((format: { Url: string; Width: number; Height: number }) => ({
    ...format,
    Url: transformUrlToProxyUrl(format.Url),
  }));
};

const getImageAILabelType = (label: number): AiLabelType | undefined => {
  if (label === 1) {
    return 'aiEdited';
  }

  if (label === 2) {
    return 'aiGenerated';
  }
};

const getRecipeImageForHeroBanner = (recipe: RecipeResult): HeroBannerImages => {
  const images: HeroBannerImages = {
    mobile: '',
    desktop: '',
    imageAlt: recipe?.Images?.[0]?.AlternativeText,
    aiLabeling: Boolean(recipe?.Images?.[0]?.AILabelingRequirement),
    aiLabelType: getImageAILabelType(recipe?.Images?.[0]?.AILabelingRequirement),
  };
  recipe?.Images?.[0]?.Formats.forEach((image: RecipeImage) => {
    if (image.Width === MOBILE_IMAGE_WIDTH && image.Height === MOBILE_IMAGE_HEIGHT) {
      images.mobile = transformUrlToProxyUrl(image.Url);
    }
    if (image.Width === DESKTOP_IMAGE_WIDTH) {
      images.desktop = transformUrlToProxyUrl(image.Url);
    }
  });
  return images;
};

const getPictureSources = (image?: any) => {
  const sources = [];
  if (image?.mobile) {
    sources.push({
      srcSet: image.mobile,
      type: 'image/webp',
      media: MOBILE_MEDIA_BP,
      width: image.width,
      height: image.height,
      mimeType: image.mimeType,
    });
  }
  if (image?.mobile === '') {
    sources.push({
      srcSet: image.tablet || image.hero || image.desktop,
      type: 'image/webp',
      media: MOBILE_MEDIA_BP,
      width: image.width,
      height: image.height,
      mimeType: image.mimeType,
    });
  }
  if (image?.desktop) {
    sources.push({
      srcSet: image.desktop,
      type: 'image/webp',
      media: DESKTOP_MEDIA_BP,
      width: image.width,
      height: image.height,
      mimeType: image.mimeType,
    });
  }
  return sources;
};

const isNextImageUnoptimized = (src: string) => {
  if (
    !src.includes(MEDIA_DOMAIN) &&
    !src.includes(RECIPE_DOMAIN) &&
    !src.includes(RECIPE_DOMAIN_AZURE) &&
    !src.includes(RECIPE_PREFIX_EDGE)
  ) {
    return false;
  }

  return true;
};

/**
 * Converts relative URLs to absolute URLs for social sharing
 * Pinterest and other social platforms require absolute URLs for images
 */
const getAbsoluteUrl = (
  url: string | undefined,
  origin: string | undefined,
): string | undefined => {
  if (!url || !origin) return undefined;

  if (url.startsWith('/')) {
    return `${origin}${url}`;
  }

  return url;
};

export const ImagesService = {
  getPictureSources,
  getRecipeImageForHeroBanner,
  transformUrlToProxyUrl,
  isNextImageUnoptimized,
  getAbsoluteUrl,
  transformRecipeImageToProxyUrl,
  getImageAILabelType,
};
